import {
  Droplets, Landmark, ShieldCheck, Layers, Box, Columns,
  DoorOpen, ArrowUpFromLine, Home, Zap, Wrench, Flame,
  PaintBucket, Building2, Armchair, Hammer,
  CircleDot, Container, Cuboid, Waves, Pipette,
  RectangleHorizontal, SquareStack, Blocks, Grid3X3,
  PanelTop, LayoutGrid, GlassWater,
  Pill, Square,
  PanelLeft, Frame, SquareAsterisk,
  ArrowUp, LayoutList,
  Cone, Triangle, Mountain, Layers3, PanelTopClose,
  CloudRain,
  Plug, Cable,
  Gauge, Heater,
  Cylinder, Fan, ThermometerSun,
  Grid2X2, Paintbrush, PaintRoller,
  Scaling, Rows3, PanelBottom,
  Blinds, Crown,
  Palette, Brush,
  CookingPot, Bath,
  DoorClosed, Scissors, Stars, Ruler, Package,
  HardHat, Trash2, Truck, Scale, Construction, Fence, Shield,
  type LucideIcon,
} from "lucide-react";

export interface CostNavItem {
  id: string;

  label: string;

  icon: LucideIcon;

  categoryFilter?: string;

  tradeFilter?: string;

  // Keep keywordFilter unchanged: single-label classification also uses it.
  keywordFilter?: string;

  /** Additional browsing classifications; never used by AI/quote label matching. */
  browseFilters?: Array<{ categoryFilter: string; tradeFilter?: string; keywordFilter?: string }>;
  /** Data-derived labels are owned by the catalogue, not saved heading preferences. */

  generated?: boolean;

  browseFilter?: CostBrowseFilter;
}

export interface CostNavSection {
  id: string;
  label: string;
  icon: LucideIcon;
  items: CostNavItem[];
  generated?: boolean;
}

export const COST_NAV_SECTIONS: CostNavSection[] = [
  {
    id: "preparation",
    label: "Preparation",
    icon: HardHat,
    items: [
      { id: "legal_fees", label: "Professional Fees", icon: Scale, categoryFilter: "preliminaries", tradeFilter: "legal_fees" },
      { id: "site_setup", label: "Site Establishment", icon: Construction, categoryFilter: "preliminaries", tradeFilter: "site_setup" },
      { id: "scaffolding_general", label: "Access & Scaffolding", icon: ArrowUpFromLine, categoryFilter: "scaffolding", tradeFilter: "scaffolding" },
      { id: "equipment_hire", label: "Plant & Equipment", icon: Wrench, categoryFilter: "preliminaries", tradeFilter: "equipment_hire" },
      { id: "welfare", label: "Welfare Facilities", icon: Shield, categoryFilter: "preliminaries", tradeFilter: "welfare" },
      { id: "fences_barriers", label: "Fencing & Barriers", icon: Fence, categoryFilter: "preliminaries", tradeFilter: "fences_barriers" },
      { id: "preparation_protection", label: "Surface Protection", icon: ShieldCheck, categoryFilter: "preliminaries", tradeFilter: "preparation_protection" },
    ],
  },
  {
    id: "demolition_removal",
    label: "Demolition & Strip Out",
    icon: Trash2,
    items: [
      { id: "structural_demolition", label: "Structural Takedown", icon: Hammer, categoryFilter: "demolition", tradeFilter: "structural_demolition" },
      { id: "strip_out", label: "Internal Strip Out", icon: Trash2, categoryFilter: "demolition", tradeFilter: "strip_out" },
      { id: "waste_removal", label: "Waste & Disposal", icon: Truck, categoryFilter: "waste", tradeFilter: "waste_removal" },
    ],
  },
  {
    id: "drainage",
    label: "Drainage",
    icon: Droplets,
    items: [
      { id: "excavation", label: "Excavation & Earthworks", icon: Layers, categoryFilter: "groundworks", tradeFilter: "excavation" },
      { id: "drain_runs", label: "Pipework Runs", icon: Pipette, categoryFilter: "groundworks", tradeFilter: "drainage" },
      { id: "aco_channels", label: "Linear Channels", icon: RectangleHorizontal, categoryFilter: "groundworks", tradeFilter: "drainage", keywordFilter: "aco channel" },
      { id: "upvc_manhole", label: "Plastic Inspection Chambers", icon: CircleDot, categoryFilter: "groundworks", tradeFilter: "drainage", keywordFilter: "upvc manhole", browseFilter: { allOf: [["upvc", "pvc", "plastic"], ["manhole", "manholes", "chamber", "chambers"]] } },
      { id: "brick_manholes", label: "Brick Chambers", icon: Box, categoryFilter: "groundworks", tradeFilter: "drainage", keywordFilter: "brick manhole", browseFilter: { allOf: [["brick", "brickwork"], ["manhole", "manholes", "chamber", "chambers"]] } },
      { id: "concrete_manholes", label: "Concrete Chambers", icon: Container, categoryFilter: "groundworks", tradeFilter: "drainage", keywordFilter: "concrete manhole", browseFilter: { allOf: [["concrete"], ["manhole", "manholes", "chamber", "chambers"]] } },
      { id: "soakaway", label: "Soakaway Systems", icon: Waves, categoryFilter: "groundworks", tradeFilter: "drainage", keywordFilter: "soakaway" },
      { id: "septic_tank", label: "Off-Mains Drainage", icon: Cuboid, categoryFilter: "groundworks", tradeFilter: "drainage", keywordFilter: "septic tank" },
    ],
  },
  {
    id: "foundation",
    label: "Foundations",
    icon: Landmark,
    items: [
      { id: "foundation", label: "Strip & Trench Fill", icon: Layers, categoryFilter: "foundations", tradeFilter: "foundations" },
      { id: "underpinning", label: "Underpinning", icon: ArrowUp, categoryFilter: "foundations", keywordFilter: "underpinning" },
      { id: "ground_screws", label: "Screw Pile Foundations", icon: SquareAsterisk, categoryFilter: "foundations", keywordFilter: "ground screw" },
      { id: "ring_beam", label: "Ring Beam", icon: Square, categoryFilter: "foundations", keywordFilter: "ring beam" },
      { id: "wall_formwork", label: "Formwork & Shuttering", icon: PanelLeft, categoryFilter: "foundations", tradeFilter: "wall_formwork" },
    ],
  },
  {
    id: "below_dpc",
    label: "Substructure",
    icon: ShieldCheck,
    items: [
      { id: "solid_walls_below_dpc", label: "Solid Masonry Below DPC", icon: Box, categoryFilter: "brickwork", keywordFilter: "below dpc solid", browseFilter: { allOf: [["below dpc", "below damp proof course", "below damp proof course level"], ["solid", "single skin", "single leaf", "one brick", "half brick"]] } },
      { id: "cavity_walls_below_dpc", label: "Cavity Masonry Below DPC", icon: SquareStack, categoryFilter: "brickwork", keywordFilter: "below dpc cavity", browseFilter: { allOf: [["below dpc", "below damp proof course", "below damp proof course level"], ["cavity"]] } },
      { id: "waterproofing", label: "Tanking & Waterproofing", icon: Droplets, categoryFilter: "damp_proofing", keywordFilter: "waterproof tanking", browseFilter: { allOf: [["tanking", "waterproof", "waterproofing", "cavity drain", "sump"]] } },
      { id: "damp_proof_injection", label: "DPC Injection", icon: Pipette, categoryFilter: "damp_proofing", keywordFilter: "damp proof injection", browseFilter: { allOf: [["injection", "inject", "injected", "injecting"], ["dpc", "damp proof", "rising damp"]] } },
    ],
  },
  {
    id: "ground_floor",
    label: "Ground Floor",
    icon: Layers,
    items: [
      { id: "timber_floors", label: "Suspended Timber", icon: LayoutGrid, categoryFilter: "carpentry", keywordFilter: "timber floor" },
      { id: "concrete_floors", label: "Concrete Slab", icon: Cuboid, categoryFilter: "foundations", tradeFilter: "concrete", keywordFilter: "concrete floor slab" },
      { id: "beam_block_floors", label: "Beam & Block", icon: Blocks, categoryFilter: "foundations", keywordFilter: "beam block floor" },
      { id: "screed", label: "Floor Screed", icon: PanelBottom, categoryFilter: "flooring", tradeFilter: "screeding" },
    ],
  },
  {
    id: "walls",
    label: "Walls & Masonry",
    icon: Building2,
    items: [
      { id: "external_walls", label: "External Masonry", icon: Layers, categoryFilter: "brickwork", keywordFilter: "external wall", browseFilter: { allOf: [["external", "exterior", "cavity", "garden", "boundary", "single skin", "single leaf"], ["wall", "walls", "walling", "brickwork", "blockwork", "masonry"]] } },
      { id: "internal_walls", label: "Internal Blockwork", icon: PanelLeft, categoryFilter: "internal_walls", tradeFilter: "block_partitions" },
      { id: "internal_wall_insulation", label: "Wall Insulation (Internal)", icon: Grid3X3, categoryFilter: "insulation", keywordFilter: "internal wall insulation" },
      { id: "concrete_walls", label: "In-Situ Concrete", icon: Cuboid, categoryFilter: "brickwork", keywordFilter: "concrete wall", browseFilter: { allOf: [["concrete wall", "concrete walls", "concrete walling", "in situ concrete", "insitu concrete", "cast concrete", "poured concrete", "reinforced concrete"], ["wall", "walls", "walling"]] } },
      { id: "stud_walls", label: "Timber Stud Partitions", icon: Grid2X2, categoryFilter: "internal_walls", tradeFilter: "stud_walls" },
      { id: "glass_block_wall", label: "Glass Block Walling", icon: GlassWater, categoryFilter: "brickwork", keywordFilter: "glass block", browseFilter: { allOf: [["glass block", "glass blocks", "glass brick", "glass bricks"]] } },
      { id: "columns", label: "Columns & Posts", icon: Columns, categoryFilter: "steel", keywordFilter: "column" },
      { id: "sip", label: "SIP Panels", icon: Layers3, categoryFilter: "insulation", keywordFilter: "sip structural insulated panel" },
      { id: "chimney", label: "Chimney Works", icon: Container, categoryFilter: "brickwork", keywordFilter: "chimney" },
      { id: "toothing_out", label: "Brick Bonding", icon: SquareStack, categoryFilter: "brickwork", keywordFilter: "toothing" },
    ],
  },
  {
    id: "structural_elements",
    label: "Structural Elements",
    icon: Columns,
    items: [
      { id: "steel_beams", label: "Steel RSJs", icon: Frame, categoryFilter: "steel", tradeFilter: "steelwork" },
      { id: "timber_beams", label: "Timber Lintels & Beams", icon: Ruler, categoryFilter: "carpentry", keywordFilter: "timber beam" },
      { id: "padstones_connections", label: "Bearing Points & Fixings", icon: Cuboid, categoryFilter: "steel", tradeFilter: "structural_supports" },
      { id: "beam_connections", label: "Connections & Anchors", icon: SquareAsterisk, categoryFilter: "steel", keywordFilter: "connection anchor end plate resin grout weld" },
      { id: "fire_protection", label: "Steel Fire Protection", icon: ShieldCheck, categoryFilter: "steel", keywordFilter: "fire protection intumescent fire rating" },
    ],
  },
  {
    id: "openings",
    label: "Doors & Windows",
    icon: DoorOpen,
    items: [
      { id: "windows", label: "Windows", icon: PanelTop, categoryFilter: "windows_doors", tradeFilter: "windows", keywordFilter: "window casement sash upvc double glazed glazing pvc" },
      { id: "window_designer", label: "Bespoke Glazing", icon: LayoutList, categoryFilter: "windows_doors", tradeFilter: "windows", keywordFilter: "designer" },
      { id: "external_doors", label: "External Doors", icon: DoorOpen, categoryFilter: "windows_doors", tradeFilter: "external_doors" },
      { id: "garage_doors", label: "Garage Doors", icon: PanelTopClose, categoryFilter: "windows_doors", keywordFilter: "garage door", browseFilter: { allOf: [["garage door", "garage doors"]] } },
      { id: "wall_openings", label: "New Openings", icon: Square, categoryFilter: "demolition", keywordFilter: "wall opening" },
      { id: "lintels", label: "Lintels & Supports", icon: RectangleHorizontal, categoryFilter: "steel", keywordFilter: "lintel" },
    ],
  },
  {
    id: "upper_floor",
    label: "First Floor",
    icon: ArrowUpFromLine,
    items: [
      { id: "upper_floor_joists", label: "Floor Joist System", icon: LayoutGrid, categoryFilter: "carpentry", keywordFilter: "floor joist upper" },
      { id: "beam_block_upper", label: "Beam & Block (Upper)", icon: Blocks, categoryFilter: "foundations", keywordFilter: "beam block upper floor" },
    ],
  },
  {
    id: "roof",
    label: "Roofing",
    icon: Home,
    items: [
      { id: "complete_roof", label: "Full Finish Roof", icon: Home, categoryFilter: "roofing", tradeFilter: "complete_roof", keywordFilter: "complete roof full finish" },
      { id: "roof_structure", label: "Roof Framing", icon: Triangle, categoryFilter: "roofing", tradeFilter: "roof_structure" },
      { id: "roof_trusses", label: "Trussed Rafters", icon: Mountain, categoryFilter: "roofing", keywordFilter: "truss" },
      { id: "dormer", label: "Dormer Construction", icon: Cone, categoryFilter: "roofing", keywordFilter: "dormer" },
      { id: "metal_roof_covering", label: "Metal Sheeting", icon: PanelTop, categoryFilter: "roofing", tradeFilter: "roof_coverings", keywordFilter: "metal" },
      { id: "profile_range", label: "Roof Coverings", icon: Layers3, categoryFilter: "roofing", tradeFilter: "roof_coverings", keywordFilter: "profile", browseFilter: { allOf: [] } },
      { id: "flat_roof_covering", label: "Flat Roof Systems", icon: PanelTopClose, categoryFilter: "roofing", keywordFilter: "flat roof", browseFilter: { allOf: [["flat roof", "flat roofs"]] } },
      { id: "roof_windows", label: "Rooflights & Skylights", icon: PanelTop, categoryFilter: "windows_doors", keywordFilter: "rooflight skylight velux roof_window" },
      { id: "roof_insulation", label: "Roof Insulation", icon: Grid3X3, categoryFilter: "insulation", keywordFilter: "roof insulation", browseFilter: { allOf: [["roof", "loft", "rafter", "rafters"], ["insulation", "insulate", "insulated"]] } },
      { id: "leadwork", label: "Leadwork & Flashings", icon: Layers, categoryFilter: "roofing", tradeFilter: "leadwork" },
      { id: "fascia_soffit", label: "Fascia, Soffit & Bargeboards", icon: Rows3, categoryFilter: "roofing", keywordFilter: "fascia soffit" },
      { id: "rainwater_goods", label: "Gutters & Downpipes", icon: CloudRain, categoryFilter: "roofing", tradeFilter: "gutters_downpipes" },
    ],
  },
  {
    id: "electrics",
    label: "Electrical",
    icon: Zap,
    items: [
      { id: "first_fix_electrics", label: "First Fix Wiring", icon: Plug, categoryFilter: "electrics", tradeFilter: "first_fix_electrics" },
      { id: "second_fix_electrics", label: "Second Fix Electrics", icon: Cable, categoryFilter: "electrics", tradeFilter: "second_fix_electrics" },
      { id: "testing_certification", label: "Testing & Certification", icon: ShieldCheck, categoryFilter: "electrics", tradeFilter: "testing_certification" },
      { id: "incoming_electrical", label: "Mains Supply Connection", icon: Zap, categoryFilter: "electrics", keywordFilter: "incoming electrical supply",
        browseFilter: { allOf: [["mains connection", "mains connections", "mains supply", "incoming supply",
          "incoming electrical", "incoming electricity", "incoming power", "electricity connection",
          "electrical supply connection", "dno"]] } },
    ],
  },
  {
    id: "plumbing",
    label: "Plumbing",
    icon: Wrench,
    items: [
      { id: "plumbing_general", label: "First Fix Pipework", icon: Wrench, categoryFilter: "plumbing", tradeFilter: "first_fix_plumbing" },
      { id: "advanced_plumbing", label: "Second Fix Plumbing", icon: Gauge, categoryFilter: "plumbing", tradeFilter: "second_fix_plumbing" },
      { id: "drainage_plumbing", label: "Below Ground Drainage", icon: Droplets, categoryFilter: "groundworks", tradeFilter: "drainage" },
      { id: "incoming_water", label: "Mains Water Connection", icon: Pipette, categoryFilter: "plumbing", keywordFilter: "incoming water mains",
        browseFilter: { allOf: [["mains water", "water mains", "incoming water", "water service connection", "water supply connection"]] } },
    ],
  },
  {
    id: "heating",
    label: "Heating & HVAC",
    icon: Flame,
    items: [
      { id: "gas_boilers", label: "Boiler Systems", icon: Flame, categoryFilter: "plumbing", tradeFilter: "heating", keywordFilter: "boiler" },
      { id: "incoming_gas", label: "Gas Supply Connection", icon: Gauge, categoryFilter: "plumbing", keywordFilter: "incoming gas",
        browseFilter: { allOf: [["mains gas", "gas mains", "incoming gas", "gas service connection", "gas supply connection"]] } },
      { id: "heating_general", label: "Central Heating", icon: ThermometerSun, categoryFilter: "plumbing", tradeFilter: "heating" },
      { id: "cylinders", label: "Hot Water Cylinders", icon: Cylinder, categoryFilter: "plumbing", keywordFilter: "cylinder" },
      { id: "radiators", label: "Radiators & Emitters", icon: Heater, categoryFilter: "plumbing", keywordFilter: "radiator" },
      { id: "ventilation_general", label: "Ventilation Systems", icon: Fan, categoryFilter: "ventilation", tradeFilter: "ventilation" },
      { id: "air_conditioning", label: "Climate Control", icon: Fan, categoryFilter: "ventilation", keywordFilter: "air conditioning" },
      { id: "underfloor_heating", label: "Underfloor Heating", icon: Grid2X2, categoryFilter: "plumbing", tradeFilter: "underfloor_heating" },
    ],
  },
  {
    id: "internal_finishes",
    label: "Internal Finishes",
    icon: PaintBucket,
    items: [
      { id: "tiling_walls", label: "Wall Tiling", icon: Grid3X3, categoryFilter: "tiling", tradeFilter: "wall_tiling" },
      { id: "tiling_floors", label: "Floor Tiling", icon: LayoutGrid, categoryFilter: "tiling", tradeFilter: "floor_tiling" },
      { id: "plastering_skim", label: "Wet Plastering", icon: PaintRoller, categoryFilter: "plastering", tradeFilter: "plastering_wet" },
      { id: "drylining", label: "Dry Lining", icon: PanelLeft, categoryFilter: "plastering", tradeFilter: "drylining" },
      { id: "wall_finishes", label: "Wall Treatments", icon: Palette, categoryFilter: "decorating", keywordFilter: "wall finish" },
      { id: "ceiling_finishes", label: "Ceiling Works", icon: PanelTop, categoryFilter: "decorating", keywordFilter: "ceiling" },
      { id: "floor_finishes", label: "Floor Coverings", icon: Scaling, categoryFilter: "flooring", tradeFilter: "floor_finishes" },
      { id: "wall_panelling", label: "Timber Panelling", icon: Blinds, categoryFilter: "carpentry", keywordFilter: "panelling panel" },
      { id: "cornice_coving", label: "Decorative Mouldings", icon: Crown, categoryFilter: "plastering", keywordFilter: "cornice coving" },
      { id: "internal_cladding", label: "Interior Linings", icon: Rows3, categoryFilter: "carpentry", keywordFilter: "internal cladding" },
      { id: "internal_decorating", label: "Painting & Decorating", icon: Brush, categoryFilter: "decorating", tradeFilter: "painting" },
      { id: "wallpapering", label: "Wallpapering", icon: Scaling, categoryFilter: "decorating", tradeFilter: "wallpapering" },
    ],
  },
  {
    id: "exterior_finishes",
    label: "External Finishes",
    icon: Building2,
    items: [
      { id: "rendering", label: "Render Systems", icon: Paintbrush, categoryFilter: "plastering", keywordFilter: "render rendering" },
      { id: "ext_wall_insulation", label: "EWI Systems", icon: Grid3X3, categoryFilter: "insulation", keywordFilter: "external wall insulation" },
      { id: "external_cladding", label: "Facade Cladding", icon: Rows3, categoryFilter: "carpentry", keywordFilter: "external cladding" },
      { id: "external_decorating", label: "Exterior Painting", icon: PaintRoller, categoryFilter: "decorating", keywordFilter: "external" },
      { id: "masonry_painting", label: "Masonry Coatings", icon: Brush, categoryFilter: "decorating", keywordFilter: "masonry paint" },
      { id: "glass_balustrades", label: "Glazed Balustrades", icon: GlassWater, categoryFilter: "carpentry", keywordFilter: "glass balustrade" },
    ],
  },
  {
    id: "fixtures_installations",
    label: "Fixtures & Installations",
    icon: Armchair,
    items: [
      { id: "making_good_general", label: "Making Good", icon: Package, categoryFilter: "making_good", tradeFilter: "making_good" },
      { id: "snagging", label: "Snagging & Defects", icon: Shield, categoryFilter: "making_good", tradeFilter: "snagging" },
      { id: "kitchen", label: "Kitchen", icon: CookingPot, categoryFilter: "kitchens", tradeFilter: "kitchen_fitting" },
      { id: "bathroom", label: "Bathroom", icon: Bath, categoryFilter: "bathrooms", tradeFilter: "bathroom_fitting" },
    ],
  },
  {
    id: "carpentry_section",
    label: "Joinery & Carpentry",
    icon: Hammer,
    items: [
      { id: "first_fix_carpentry", label: "First Fix Carpentry", icon: Hammer, categoryFilter: "carpentry", tradeFilter: "first_fix_carpentry" },
      { id: "internal_doors", label: "Door Replacement", icon: DoorClosed, categoryFilter: "carpentry", tradeFilter: "second_fix_carpentry", keywordFilter: "replace replacement existing door", browseFilter: { nameAnyOf: ["door", "doors"], allOf: [["replace", "replacement", "rehang", "re-hang"]] } },
      { id: "internal_doors_new", label: "New Door Installation", icon: DoorOpen, categoryFilter: "carpentry", tradeFilter: "second_fix_carpentry", keywordFilter: "new installation internal door frame", browseFilter: { nameAnyOf: ["door", "doors"], allOf: [["internal", "lining", "frame"], ["new", "install", "installation", "hang", "hanging"]] } },
      { id: "joinery_workshop", label: "Bespoke Joinery", icon: Scissors, categoryFilter: "carpentry", keywordFilter: "joinery workshop" },
      { id: "stairs", label: "Staircases", icon: Stars, categoryFilter: "carpentry", keywordFilter: "stairs staircase" },
      { id: "skirting_boards", label: "Skirting & Trim", icon: Ruler, categoryFilter: "carpentry", tradeFilter: "second_fix_carpentry", keywordFilter: "skirting" },
      { id: "architraves", label: "Door Surrounds", icon: Frame, categoryFilter: "carpentry", tradeFilter: "second_fix_carpentry", keywordFilter: "architrave" },
      { id: "window_boards", label: "Sills & Boards", icon: PanelBottom, categoryFilter: "carpentry", keywordFilter: "window board sill" },
      { id: "loft_boarding", label: "Loft Flooring", icon: LayoutGrid, categoryFilter: "carpentry", keywordFilter: "loft boarding" },
      { id: "loft_hatch", label: "Loft Access", icon: PanelTopClose, categoryFilter: "carpentry", keywordFilter: "loft hatch" },
      { id: "boxing_in", label: "Pipe Boxing", icon: Package, categoryFilter: "carpentry", keywordFilter: "boxing" },
    ],
  },
  {
    id: "other_section",
    label: "Other",
    icon: Package,
    items: [
      { id: "other_general", label: "Miscellaneous", icon: Package, categoryFilter: "other", tradeFilter: "other" },
    ],
  },
];

/** Browsing-only evidence: AND between groups, OR within each group. */
export interface CostBrowseFilter {
  allOf: string[][];
  nameAnyOf?: string[];
}
